import { useState, useEffect } from "react";
import { Shield, Smartphone, Mail, CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MFASetupPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("choose"); // choose, setup-totp, setup-email, verify
  const [qrData, setQrData] = useState(null);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/partner/mfa/status`, { withCredentials: true })
      .then(res => {
        setStatus(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const setupTOTP = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/partner/mfa/setup`, { mfa_method: "totp" }, { withCredentials: true });
      setQrData(res.data);
      setStep("setup-totp");
    } catch {
      toast.error("Failed to start TOTP setup");
    } finally {
      setSubmitting(false);
    }
  };

  const setupEmail = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/mfa/setup`, { mfa_method: "email" }, { withCredentials: true });
      setStatus({ mfa_enabled: true, mfa_method: "email" });
      setStep("done");
      toast.success("Email MFA enabled. You will receive a code at each login.");
    } catch {
      toast.error("Failed to enable email MFA");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyTOTP = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/partner/mfa/verify-setup`, { code }, { withCredentials: true });
      setStatus({ mfa_enabled: true, mfa_method: "totp" });
      setStep("done");
      toast.success("Authenticator app MFA enabled successfully");
    } catch {
      toast.error("Invalid code. Check your authenticator app and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-warm-600" /></div>;

  // Already enabled
  if (status?.mfa_enabled && step !== "done") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6" data-testid="mfa-enabled">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle className="w-6 h-6 text-green-700" />
          <h3 className="font-semibold text-green-900">MFA Enabled</h3>
        </div>
        <p className="text-sm text-green-800">
          Your account is secured with {status.mfa_method === "totp" ? "authenticator app (TOTP)" : "email"} verification.
          A code will be required each time you log in.
        </p>
      </div>
    );
  }

  // Setup complete
  if (step === "done") {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6" data-testid="mfa-setup-complete">
        <div className="flex items-center gap-3 mb-2">
          <CheckCircle className="w-6 h-6 text-green-700" />
          <h3 className="font-semibold text-green-900">MFA Successfully Enabled</h3>
        </div>
        <p className="text-sm text-green-800">
          Your account is now protected with {status?.mfa_method === "totp" ? "authenticator app" : "email"} verification.
        </p>
      </div>
    );
  }

  // TOTP QR setup
  if (step === "setup-totp" && qrData) {
    return (
      <div className="space-y-4" data-testid="mfa-totp-setup">
        <div className="flex items-center gap-2 mb-2">
          <Smartphone className="w-5 h-5 text-warm-700" />
          <h3 className="font-semibold text-gray-900">Set Up Authenticator App</h3>
        </div>
        <div className="bg-gray-50 rounded-xl p-6 text-center">
          <p className="text-sm text-gray-600 mb-4">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
          <img src={`data:image/png;base64,${qrData.qr_code}`} alt="QR Code" className="mx-auto mb-4 w-48 h-48" />
          <p className="text-xs text-gray-400 break-all">Manual key: {qrData.secret}</p>
        </div>
        <form onSubmit={verifyTOTP} className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 block mb-1">Enter 6-digit code from your app</label>
            <input className="w-full border rounded-lg px-3 py-2 text-center text-lg tracking-widest font-mono"
              value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} pattern="[0-9]{6}"
              placeholder="000000" required autoFocus data-testid="mfa-totp-code-input" />
          </div>
          <Button type="submit" disabled={submitting || code.length !== 6} className="bg-warm-700 hover:bg-warm-800 text-white">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
          </Button>
        </form>
      </div>
    );
  }

  // Choose method
  return (
    <div className="space-y-4" data-testid="mfa-setup-choose">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-5 h-5 text-orange-600" />
        <h3 className="font-semibold text-gray-900">Multi-Factor Authentication Required</h3>
      </div>
      <p className="text-sm text-gray-600">
        MFA is required for all partner accounts that access sensitive data. Choose your preferred verification method:
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        <button onClick={setupTOTP} disabled={submitting}
          className="border border-gray-200 rounded-xl p-5 text-left hover:border-warm-300 hover:bg-warm-50/50 transition-colors"
          data-testid="mfa-choose-totp">
          <Smartphone className="w-8 h-8 text-warm-700 mb-3" />
          <h4 className="font-semibold text-gray-900 text-sm mb-1">Authenticator App</h4>
          <p className="text-xs text-gray-500">Use Google Authenticator, Authy, or any TOTP app. Generates a new code every 30 seconds. Most secure option.</p>
        </button>
        <button onClick={setupEmail} disabled={submitting}
          className="border border-gray-200 rounded-xl p-5 text-left hover:border-warm-300 hover:bg-warm-50/50 transition-colors"
          data-testid="mfa-choose-email">
          <Mail className="w-8 h-8 text-warm-700 mb-3" />
          <h4 className="font-semibold text-gray-900 text-sm mb-1">Email Verification</h4>
          <p className="text-xs text-gray-500">Receive a 6-digit code via email each time you log in. Simpler setup, no app required.</p>
        </button>
      </div>
    </div>
  );
};
